import { Card } from "@tremor/react";
import { Popover, Spin } from "antd";
import dayjs from "dayjs";

import { type ExtendedKpi, type KpiClientDetail } from "./hooks/useTrainerKpiData";

export type TrainerKpiRow = {
  /** Month key, YYYY-MM. */
  month: string;
  stats?: ExtendedKpi;
};

interface TrainerKPICardProps {
  /** One row per month, workbook-style; a single-element array renders the classic single-month view. */
  rows: TrainerKpiRow[];
  title?: string;
  loading?: boolean;
}

const monthLabel = (month: string) =>
  dayjs(`${month}-01`).toDate().toLocaleDateString("pl-PL", { month: "long" }).toUpperCase();

const hasBundle = (item: unknown): item is { bundleName: string; purchaseDate: string; amount: number } => {
  return Boolean(item) && typeof item === "object" && item !== null && "bundleName" in item;
};

function ClientList({ clients, header }: { clients: KpiClientDetail[]; header: string }) {
  return (
    <div className="max-h-[500px] max-w-md overflow-y-auto">
      <div className="mb-2 font-medium">{header}</div>
      <div className="space-y-1">
        {clients.map((client, index) => (
          <div key={index} className="mb-2 border-b border-gray-200 pb-2 text-xs last:mb-0 last:border-b-0 last:pb-0">
            <div className="mb-1 flex items-center gap-2">
              <span className="font-medium">{client.traineeEmail}</span>
              <span
                className={`rounded px-1 text-xs ${
                  client.status === "renewed" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                }`}
              >
                {client.status === "renewed" ? "Przedłużyła" : "Nie przedłużyła"}
              </span>
              <span
                className={`rounded px-1 text-xs ${
                  client.isGhost ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800"
                }`}
              >
                {client.isGhost ? "Duch" : "Aktywna"}
              </span>
            </div>
            {client.bundleHistory && client.bundleHistory.length > 0 && (
              <div className="ml-2 mt-1">
                <div className="mb-1 font-medium text-gray-500">Historia zakupów:</div>
                {client.bundleHistory.filter(hasBundle).map((bundle, bundleIndex) => (
                  <div key={bundleIndex} className="text-xs text-gray-600">
                    • {bundle.bundleName} - {new Date(bundle.purchaseDate).toLocaleDateString("pl-PL")} -{" "}
                    {bundle.amount}zł
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function TrainerKPICard({ rows, title = "Przedłużenia", loading = false }: TrainerKPICardProps) {
  if (loading) {
    return (
      <Card className="w-full">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
            KPI Trenera - {title}
          </h3>
        </div>
        <div className="flex justify-center py-8">
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
          KPI Trenera - {title}
        </h3>
      </div>

      <div className="mt-6">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="bg-orange-500 px-3 py-2 text-left text-sm font-medium uppercase text-gray-600 text-white dark:text-gray-400">
                  {title}
                </th>
                <th className="border-l border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-900 dark:border-gray-700 dark:text-gray-100">
                  Ilość osób w danym miesiącu kończyło
                </th>
                <th className="border-l border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-900 dark:border-gray-700 dark:text-gray-100">
                  ile osób z tych co kończyło przedłużyło
                </th>
                <th className="border-l border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-900 dark:border-gray-700 dark:text-gray-100">
                  ile z tych osób było duchami
                </th>
                <th className="border-l border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-900 dark:border-gray-700 dark:text-gray-100">
                  ile z tych osób było aktywnymi
                </th>
                <th className="border-l border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-900 dark:border-gray-700 dark:text-gray-100">
                  ile % z duchem
                </th>
                <th className="border-l border-gray-200 px-3 py-2 text-center text-sm font-medium text-gray-900 dark:border-gray-700 dark:text-gray-100">
                  ile % bez ducha
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const stats = row.stats;
                const hasData = (stats?.totalEnding ?? 0) > 0;
                const details = stats?.detatils ?? [];
                return (
                  <tr
                    key={row.month}
                    className="border-b border-gray-200 bg-cyan-100 dark:border-gray-700 dark:bg-cyan-900"
                  >
                    <td className="bg-cyan-200 px-3 py-2 text-sm font-medium dark:bg-cyan-800">
                      {monthLabel(row.month)}
                    </td>
                    <Popover
                      content={<ClientList clients={details} header="Klienci którzy kończyli w tym miesiącu:" />}
                      placement="top"
                      trigger="hover"
                    >
                      <td className="cursor-help border-l border-gray-200 px-3 py-2 text-center text-sm hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800">
                        {stats?.totalEnding ?? 0}
                      </td>
                    </Popover>
                    <Popover
                      content={
                        <ClientList
                          clients={details.filter((client) => client.status === "renewed")}
                          header="Klienci którzy przedłużyli:"
                        />
                      }
                      placement="top"
                      trigger="hover"
                    >
                      <td className="cursor-help border-l border-gray-200 px-3 py-2 text-center text-sm hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800">
                        {stats?.totalRenewed ?? 0}
                      </td>
                    </Popover>
                    <td className="border-l border-gray-200 px-3 py-2 text-center text-sm dark:border-gray-700">
                      {stats?.ghostRenewals ?? 0}
                    </td>
                    <td className="border-l border-gray-200 px-3 py-2 text-center text-sm dark:border-gray-700">
                      {stats?.activeRenewals ?? 0}
                    </td>
                    <td
                      className={`border-l border-gray-200 px-3 py-2 text-center text-sm dark:border-gray-700 ${
                        !hasData
                          ? ""
                          : stats!.totalRenewalsPercentage === 0
                            ? "bg-red-500 text-white"
                            : "bg-green-500 text-white"
                      }`}
                    >
                      {hasData ? `${stats!.totalRenewalsPercentage.toFixed(2)}%` : "—"}
                    </td>
                    <td
                      className={`border-l border-gray-200 px-3 py-2 text-center text-sm dark:border-gray-700 ${
                        !hasData
                          ? ""
                          : stats!.activeRenewalsPercentage === 0
                            ? "bg-red-500 text-white"
                            : "bg-green-500 text-white"
                      }`}
                    >
                      {hasData ? `${stats!.activeRenewalsPercentage.toFixed(2)}%` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}
